<template>
  <v-app>
    <div class="container">
      <!-- Custom Admin Navbar -->
      <v-app-bar app flat elevation="0" class="navbar">
        <v-container class="pa-0 ma-0" fluid>
          <div class="d-flex justify-space-between align-center px-4 w-100">
            <v-app-bar-title class="nav-title">Home</v-app-bar-title>
            
            <!-- Profile Icon with Logout Option -->
            <div>
              <v-menu offset-y transition="slide-y-transition">
                <template v-slot:activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-account"
                    class="profile-btn"
                    variant="text"
                  >
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item @click="handleDirectLogout" class="logout-item">
                    <v-list-item-title>Logout</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </div>
        </v-container>
      </v-app-bar>

      <!-- Main Content with proper margin-top to avoid being covered by the navbar -->
      <v-main>
        <div class="content-container">
          <!-- Header Section -->
          <v-card class="header-section">
            <!-- Row 1: Title -->
            <div class="header-row">
              <h1 class="page-title">Menu Management</h1>
            </div>

            <!-- Row 2: Buttons and Search -->
            <div class="header-row buttons-search-row">
              <div class="button-wrapper">
                <v-btn v-if="currentLevel === 'menu'" color="primary" @click="addMenu">New Menu Item</v-btn>
                <v-btn v-if="currentLevel === 'submenu'" color="primary" @click="addSubmenu">New Submenu Item</v-btn>
                <v-btn v-if="currentLevel === 'page'" color="primary" @click="addPage">New Page Item</v-btn>
                <v-btn v-if="currentLevel === 'pagecontent'" color="primary" @click="addPageContent">New Page Content
                  Item</v-btn>
                <v-btn v-if="currentLevel === 'subpage'" color="primary" @click="addSubpage">New Subpage Item</v-btn>
                <v-btn v-if="currentLevel === 'subpagecontent'" color="primary" @click="addSubpageContent">New Subpage Content
                  Item</v-btn>

                <v-btn v-if="currentLevel !== 'menu'" color="secondary" @click="goBack">Back</v-btn>
              </div>
              <div class="search-wrapper">
                <v-text-field v-model="search" label="Search" prepend-inner-icon="mdi-magnify" variant="outlined" hide-details
                  single-line dense class="search-input"></v-text-field>
              </div>
            </div>
          </v-card>

          <!-- Menu Table -->
          <div class="table-container">
            <v-data-table v-if="currentLevel === 'menu'" :headers="menuHeaders" :items="menus" :search="search"
              :items-per-page="15" item-value="menuId" class="elevation-1 overflow-auto" dense :width="'100%'"
              :height="'calc(100vh - 280px)'" :fixed-header="true" @click:row="selectMenu">
              <!-- Slot for Actions -->
              <template v-slot:[`item.actions`]="{ item }">
                <div class="action-buttons">

                  <!-- Edit Button with Tooltip -->
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on" @click.stop="editMenu(item)">
                        <v-icon>mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>Edit</span>
                  </v-tooltip>

                  <!-- Toggle Active Button -->
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on" @click.stop="toggleMenuActive(item)"
                        :class="{ 'btn-active': item.isActive, 'btn-inactive': !item.isActive }">
                        <!-- Show a different icon based on isActive -->
                        <v-icon v-if="item.isActive">mdi-toggle-switch</v-icon>
                        <v-icon v-else>mdi-toggle-switch-off</v-icon>
                      </v-btn>
                    </template>
                    <!-- If you want a tooltip text -->
                    <span>Toggle Active</span>
                  </v-tooltip>
                </div>
              </template>
            </v-data-table>
          </div>


          <!-- Submenu Table -->
          <div class="table-container">
            <v-data-table v-if="currentLevel === 'submenu'" :headers="submenuHeaders" :items="currentMenu?.submenus || []"
              :search="search" item-value="subMenuId" class="elevation-1 overflow-auto" dense :width="'100%'"
              :height="'calc(100vh - 280px)'" :fixed-header="true" @click:row="selectSubmenu">
              <template v-slot:[`item.actions`]="{ item }">
                <div class="action-buttons">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on" @click.stop="editSubmenu(item)">
                        <v-icon>mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>Edit</span>
                  </v-tooltip>

                  <!-- Toggle Active Button -->
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on" @click.stop="toggleSubmenuActive(item)"
                        :class="{ 'btn-active': item.isActive, 'btn-inactive': !item.isActive }">
                        <!-- Show different icons based on isActive -->
                        <v-icon v-if="item.isActive">mdi-toggle-switch</v-icon>
                        <v-icon v-else>mdi-toggle-switch-off</v-icon>
                      </v-btn>
                    </template>
                    <span>Toggle Active</span>
                  </v-tooltip>
                </div>
              </template>

              <template v-slot:[`item.subMenuDescription`]="{ item }">
                <div class="wrap-text">{{ item.subMenuDescription }}</div>
              </template>
            </v-data-table>
          </div>


          <!-- Page Table -->
          <div class="table-container">
            <v-data-table v-if="currentLevel === 'page'" :headers="pageHeaders" :items="currentSubmenu?.pages || []"
              :search="search" item-value="pageId" class="elevation-1 overflow-auto" dense :width="'100%'"
              :height="'calc(100vh - 280px)'" :fixed-header="true" @click:row="selectPage">

              <template v-slot:[`item.actions`]="{ item }">
                <div class="action-buttons">

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on" @click.stop="editPage(item)">
                        <v-icon>mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>Edit</span>
                  </v-tooltip>

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on" @click.stop="togglePageActive(item)"
                        :class="{ 'btn-active': item.isActive, 'btn-inactive': !item.isActive }">
                        <v-icon v-if="item.isActive">mdi-toggle-switch</v-icon>
                        <v-icon v-else>mdi-toggle-switch-off</v-icon>
                      </v-btn>
                    </template>
                    <span>Toggle Active</span>
                  </v-tooltip>
                </div>
              </template>

              <template v-slot:[`item.pageDescription`]="{ item }">
                <div class="wrap-text">{{ item.pageDescription }}</div>
              </template>
            </v-data-table>
          </div>

          <!-- Page Content -->
          <div class="table-container">
            <v-data-table v-if="currentLevel === 'pagecontent'" :headers="pageContentHeaders"
              :items="currentPage?.pagecontents || []" :search="search" item-value="pageContentId"
              class="elevation-1 overflow-auto" dense :width="'100%'" :height="'calc(100vh - 280px)'" :fixed-header="true"
              @click:row="selectPageContent">
              <template v-slot:[`item.link`]="{ item }">
                <v-tooltip bottom v-if="!item.showFullLink">
                  <!-- Tooltip activator for truncated link -->
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on" class="truncate-text" @click.stop="toggleShowFullLink(item)">
                      {{ truncate(item.link) }}
                    </span>
                  </template>
                  <!-- Full link in the tooltip -->
                  <span>{{ item.link }}</span>
                </v-tooltip>
                <!-- Full link displayed when showFullLink is true -->
                <span class="expanded-text" v-else @click.stop="toggleShowFullLink(item)">
                  {{ item.link }}
                </span>
                <!-- Subtle Copy Icon -->
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" @click.stop="copyToClipboard(item.link)" class="subtle-icon ml-2">
                      mdi-content-copy
                    </v-icon>
                  </template>
                  <span>Copy to clipboard</span>
                </v-tooltip>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <div class="action-buttons">

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on" @click.stop="editPageContent(item)">
                        <v-icon>mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>Edit</span>
                  </v-tooltip>

                  <!-- Toggle Active Button -->
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on" @click.stop="togglePageContentActive(item)"
                        :class="{ 'btn-active': item.isActive, 'btn-inactive': !item.isActive }">
                        <!-- Show different icons based on isActive -->
                        <v-icon v-if="item.isActive">mdi-toggle-switch</v-icon>
                        <v-icon v-else>mdi-toggle-switch-off</v-icon>
                      </v-btn>
                    </template>
                    <span>Toggle Active</span>
                  </v-tooltip>
                </div>
              </template>

              <template v-slot:[`item.itemDescription`]="{ item }">
                <div class="wrap-text">{{ item.itemDescription }}</div>
              </template>
            </v-data-table>
          </div>

          <!-- Subpage -->
          <div class="table-container">
            <v-data-table v-if="currentLevel === 'subpage'" :headers="subPageHeaders"
              :items="currentPageContent?.subpages || []" :search="search" item-value="subPageId"
              class="elevation-1 overflow-auto" dense :width="'100%'" :height="'calc(100vh - 280px)'" :fixed-header="true"
              @click:row="selectSubpage">
              <template v-slot:[`item.actions`]="{ item }">
                <div class="action-buttons">

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on" @click.stop="editSubpage(item)">
                        <v-icon>mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>Edit</span>
                  </v-tooltip>

                  <!-- Toggle Active Button -->
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on" @click.stop="toggleSubpageActive(item)"
                        :class="{ 'btn-active': item.isActive, 'btn-inactive': !item.isActive }">
                        <!-- Show different icons based on isActive -->
                        <v-icon v-if="item.isActive">mdi-toggle-switch</v-icon>
                        <v-icon v-else>mdi-toggle-switch-off</v-icon>
                      </v-btn>
                    </template>
                    <span>Toggle Active</span>
                  </v-tooltip>
                </div>
              </template>
              <template v-slot:[`item.subPageDescription`]="{ item }">
                <div class="wrap-text">{{ item.subPageDescription }}</div>
              </template>
            </v-data-table>
          </div>

          <!-- Subpage Content -->
          <div class="table-container">
            <v-data-table v-if="currentLevel === 'subpagecontent'" :headers="subPageContentHeaders"
              :items="currentSubpage?.subpagecontents || []" :search="search" item-value="subPageContentId"
              class="elevation-1 overflow-auto" dense :width="'100%'" :height="'calc(100vh - 280px)'" :fixed-header="true">
              <template v-slot:[`item.actions`]="{ item }">
                <div class="action-buttons">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on" @click.stop="editSubpageContent(item)">
                        <v-icon>mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>Edit</span>
                  </v-tooltip>

                  <!-- Toggle Active Button -->
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on" @click.stop="toggleSubpageContentActive(item)"
                        :class="{ 'btn-active': item.isActive, 'btn-inactive': !item.isActive }">
                        <!-- Show different icons based on isActive -->
                        <v-icon v-if="item.isActive">mdi-toggle-switch</v-icon>
                        <v-icon v-else>mdi-toggle-switch-off</v-icon>
                      </v-btn>
                    </template>
                    <span>Toggle Active</span>
                  </v-tooltip>
                </div>
              </template>
              <template v-slot:[`item.link`]="{ item }">
                <v-tooltip bottom v-if="!item.showFullLink">
                  <!-- Tooltip activator for truncated link -->
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on" class="truncate-text" @click.stop="toggleShowFullLink(item)">
                      {{ truncate(item.link) }}
                    </span>
                  </template>
                  <!-- Full link in the tooltip -->
                  <span>{{ item.link }}</span>
                </v-tooltip>
                <!-- Full link displayed when showFullLink is true -->
                <span class="expanded-text" v-else @click.stop="toggleShowFullLink(item)">
                  {{ item.link }}
                </span>
                <!-- Subtle Copy Icon -->
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" @click.stop="copyToClipboard(item.link)" class="subtle-icon ml-2">
                      mdi-content-copy
                    </v-icon>
                  </template>
                  <span>Copy to clipboard</span>
                </v-tooltip>
              </template>
              <template v-slot:[`item.itemDescription`]="{ item }">
                <div class="wrap-text">{{ item.itemDescription }}</div>
              </template>
            </v-data-table>
          </div>

          <!-- MODAL -->
          <!-- MENU -->
          <!-- Add/Edit Menu Modal -->
          <v-dialog v-model="isMenuModalOpen" max-width="500px">
            <v-card>
              <v-card-title>
                {{ isEditMenu ? 'Edit Menu' : 'Add Menu' }}
              </v-card-title>

              <!-- v-form wraps both the fields and the actions -->
              <v-form ref="menuForm" @submit.prevent="saveMenu">
                <v-card-text>
                  <!-- Your fields here -->
                  <v-text-field v-model="newMenu.menuItem" label="Menu Item" :rules="[rules.required]" />
                  <v-text-field v-model="newMenu.sortOrder" label="Sort Order"
                    :rules="[rules.numeric, rules.uniqueSortOrder]" />
                  <v-switch v-model="newMenu.isActive" :color="newMenu.isActive ? 'green' : 'grey'" label="Active" />
                  <v-text-field v-model="newMenu.link" label="Developer Link" :rules="[rules.optional]" />
                  <v-text-field v-model="newMenu.updatedBy" label="Updated By" :rules="[rules.optional]" />
                </v-card-text>

                <v-card-actions>
                  <!-- Use v-spacer to push the buttons to the right -->
                  <v-spacer />

                  <!-- Cancel Button -->
                  <v-btn text @click="handleCancel('menu')">Cancel</v-btn>

                  <!-- Save Button (type="submit") triggers @submit on Enter or click -->
                  <v-btn text color="primary" type="submit">
                    Save
                  </v-btn>
                </v-card-actions>
              </v-form>
            </v-card>
          </v-dialog>

          <!-- SUBMENU -->
          <!-- Add/Edit Submenu Modal -->
          <v-dialog v-model="isSubmenuModalOpen" max-width="500px">
            <v-card>
              <v-card-title>{{ isEditSubmenu ? 'Edit Submenu' : 'Add Submenu' }}</v-card-title>

              <!-- Wrap everything in v-form, just like the Menu modal -->
              <v-form ref="submenuForm" @submit.prevent="saveSubmenu">
                <v-card-text>
                  <v-text-field v-model="newSubmenu.subMenuItem" label="Submenu Item" :rules="[rules.required]" />

                  <v-text-field v-model="newSubmenu.subMenuDescription" label="Submenu Description"
                    :rules="[rules.optional]" />

                  <v-switch v-model="newSubmenu.isActive" :color="newSubmenu.isActive ? 'green' : 'grey'" label="Active" />

                  <!-- Updated the link field's placeholder to 'Developer Link' here -->
                  <v-text-field v-model="newSubmenu.link" label="Developer Link" :rules="[rules.required]" />

                </v-card-text>

                <v-card-actions>
                  <!-- Use <v-spacer> so that Cancel and Save appear on the right -->
                  <v-spacer />

                  <!-- Cancel button calls a method that closes the dialog -->
                  <v-btn text @click="handleCancel('submenu')">Cancel</v-btn>

                  <!-- Save button has type="submit" to allow Enter key submission -->
                  <v-btn text color="primary" type="submit">
                    Save
                  </v-btn>
                </v-card-actions>
              </v-form>
            </v-card>
          </v-dialog>

          <!-- PAGE -->
          <!-- Add/Edit Page Modal -->
          <v-dialog v-model="isPageModalOpen" max-width="500px">
            <v-card>
              <v-card-title>{{ isEditPage ? 'Edit Page' : 'Add Page' }}</v-card-title>

              <v-form ref="pageForm" @submit.prevent="savePage">
                <v-card-text>
                  <v-text-field v-model="newPage.pageName" label="Page Name" :rules="[rules.required]" />
                  <v-text-field v-model="newPage.pageTitle" label="Page Title" :rules="[rules.optional]" />
                  <v-textarea v-model="newPage.pageDescription" label="Description" :rules="[rules.optional]" />
                  <v-switch v-model="newPage.isActive" :color="newPage.isActive ? 'green' : 'grey'" label="Active" />
                </v-card-text>

                <v-card-actions>
                  <v-spacer />
                  <v-btn text @click="handleCancel('page')">Cancel</v-btn>
                  <v-btn text color="primary" type="submit">Save</v-btn>
                </v-card-actions>
              </v-form>
            </v-card>
          </v-dialog>

          <!-- PAGE CONTENT -->
          <!-- Add/Edit Page Content Modal -->
          <v-dialog v-model="isPageContentModalOpen" max-width="500px">
            <v-card>
              <v-card-title>{{ isEditPageContent ? 'Edit Page Content' : 'Add Page Content' }}</v-card-title>
              <v-form ref="pageContentForm" @submit.prevent="savePageContent">
                <v-card-text>
                  <v-text-field v-model="newPageContent.item" label="Item Name" :rules="[rules.required]" />
                  <v-textarea v-model="newPageContent.itemDescription" label="Description" :rules="[rules.optional]" />
                  <v-text-field v-model="newPageContent.link" label="Link" :rules="[rules.optional]" />
                  <!-- NOTES: Changed from v-text-field to v-select -->
                  <v-select v-model="newPageContent.contentBlock" :items="blockContentTypes" label="Content Type"
                    :rules="[rules.optional]" />
                  <v-text-field v-model="newPageContent.updatedBy" label="Updated By" :rules="[rules.optional]" />
                  <v-switch v-model="newPageContent.isActive" :color="newPageContent.isActive ? 'green' : 'grey'"
                    label="Active" />
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn text @click="handleCancel('pageContent')">Cancel</v-btn>
                  <v-btn text color="primary" type="submit">Save</v-btn>
                </v-card-actions>
              </v-form>
            </v-card>
          </v-dialog>

          <!-- SUBPAGE -->
          <!-- Add/Edit SubPage Modal -->
          <v-dialog v-model="isSubpageModalOpen" max-width="500px">
            <v-card>
              <v-card-title>{{ isEditSubpage ? 'Edit Subpage' : 'Add Subpage' }}</v-card-title>
              <v-form ref="subpageForm" @submit.prevent="saveSubpage">
                <v-card-text>
                  <v-text-field v-model="newSubpage.subPageName" label="Subpage Name" :rules="[rules.required]" />
                  <v-text-field v-model="newSubpage.subPageTitle" label="Subpage Title" :rules="[rules.optional]" />
                  <v-textarea v-model="newSubpage.subPageDescription" label="Description" :rules="[rules.optional]" />
                  <v-text-field v-model="newSubpage.updatedBy" label="Updated By" :rules="[rules.optional]" />
                  <v-switch v-model="newSubpage.isActive" :color="newSubpage.isActive ? 'green' : 'grey'" label="Active" />
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn text @click="handleCancel('subpage')">Cancel</v-btn>
                  <v-btn text color="primary" type="submit">Save</v-btn>
                </v-card-actions>
              </v-form>
            </v-card>
          </v-dialog>

          <!-- SUBPAGE CONTENT -->
          <!-- Add/Edit SubPage Content Modal -->
          <v-dialog v-model="isSubpageContentModalOpen" max-width="500px">
            <v-card>
              <v-card-title>{{ isEditSubpageContent ? 'Edit Subpage Content' : 'Add Subpage Content' }}</v-card-title>
              <v-form ref="subpageContentForm" @submit.prevent="saveSubpageContent">
                <v-card-text>
                  <v-text-field v-model="newSubpageContent.item" label="Item Name" :rules="[rules.required]" />
                  <v-textarea v-model="newSubpageContent.itemDescription" label="Description" :rules="[rules.optional]" />
                  <v-text-field v-model="newSubpageContent.link" label="Link" :rules="[rules.optional]" />
                  <!-- NOTES: Changed from v-text-field to v-select -->
                  <v-select v-model="newSubpageContent.contentBlock" :items="blockContentTypes" label="Content Type"
                    :rules="[rules.optional]" />
                  <v-text-field v-model="newSubpageContent.updatedBy" label="Updated By" :rules="[rules.optional]" />
                  <v-switch v-model="newSubpageContent.isActive" :color="newSubpageContent.isActive ? 'green' : 'grey'"
                    label="Active" />
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn text @click="handleCancel('subpageContent')">Cancel</v-btn>
                  <v-btn text color="primary" type="submit">Save</v-btn>
                </v-card-actions>
              </v-form>
            </v-card>
          </v-dialog>

          <!-- Confirmation Dialog (when add/edit/delete) -->
          <v-dialog v-model="isConfirmDialogOpen" max-width="400">
            <v-card>
              <!-- Wrap everything in a v-form -->
              <v-form @submit.prevent="confirmAction">
                <v-card-title class="text-h5">{{ confirmDialog.title }}</v-card-title>
                <v-card-text>{{ confirmDialog.message }}</v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <!-- Cancel just closes the dialog -->
                  <v-btn text @click="closeConfirmDialog">Cancel</v-btn>

                  <!-- Make this button type="submit" to capture Enter key -->
                  <v-btn text color="red" type="submit">
                    Confirm
                  </v-btn>
                </v-card-actions>
              </v-form>
            </v-card>
          </v-dialog>
        </div>
      </v-main>
    </div>
  </v-app>
</template>

<script>
import axios from "axios";
import { ref, onMounted } from "vue";

export default {
  components: {
  },
  setup() {

    // Fetch data and structure
    const axiosInstance = axios.create({
      baseURL: process.env.VUE_APP_API_URL,
      timeout: 20000, // 20s
      headers: {
        'Content-Type': 'application/json'
      },
      withCredentials: true, // ensure this is false if cookies are not used 
    });

    // State variables
    const menus = ref([]);
    const existingSortOrders = ref(null);

    const currentMenu = ref(null);
    const currentSubmenu = ref(null);
    const currentPage = ref(null);
    const currentPageContent = ref(null);
    const currentSubpage = ref(null);
    const currentLevel = ref("menu");
    const search = ref('')

    // breadcrumbs
    const breadcrumbs = ref([]);

    // Update breadcrumbs
    const updateBreadcrumbs = (level, label) => {
      // Ensure breadcrumbs start with Home
      const baseBreadcrumb = [{ level: 0, label: "Home" }];

      // Trim breadcrumbs to the current level (excluding Home)
      const updatedBreadcrumbs = breadcrumbs.value.slice(1, level);

      // Update breadcrumbs with Home as the first item
      breadcrumbs.value = baseBreadcrumb.concat(updatedBreadcrumbs);
      breadcrumbs.value.push({ level, label });
    };


    const navigateTo = (level) => {
      if (level === 0) {
        // Home level: reset everything
        breadcrumbs.value = [{ level: 0, label: "Home" }];
        currentLevel.value = "menu";
        currentMenu.value = null;
        currentSubmenu.value = null;
        currentPage.value = null;
        currentPageContent.value = null;
        currentSubpage.value = null;
        return;
      }

      // Remove all breadcrumbs deeper than the clicked level
      breadcrumbs.value = breadcrumbs.value.slice(0, level + 1);

      // Update currentLevel and clear corresponding state
      switch (level) {
        case 1:
          currentLevel.value = "submenu";
          currentSubmenu.value = null;
          currentPage.value = null;
          currentPageContent.value = null;
          currentSubpage.value = null;
          break;

        case 2:
          currentLevel.value = "page";
          currentPage.value = null;
          currentPageContent.value = null;
          currentSubpage.value = null;
          break;

        case 3:
          currentLevel.value = "pagecontent";
          currentPageContent.value = null;
          currentSubpage.value = null;
          break;

        case 4:
          currentLevel.value = "subpage";
          currentSubpage.value = null;
          break;

        default:
          console.error("Invalid breadcrumb level clicked");
      }
    };



    // add/edit/delete 
    // Menu State
    const isMenuModalOpen = ref(false); // Controls menu modal visibility
    const isEditMenu = ref(false);      // Distinguish add vs edit
    const newMenu = ref({
      menuId: "",
      menuItem: "",
      sortOrder: "",
      isActive: true,
      link: "",
      updatedBy: "",
    });


    // Submenu State
    const isSubmenuModalOpen = ref(false);
    const isEditSubmenu = ref(false); // Distinguish between add and edit
    const newSubmenu = ref({
      subMenuItem: "",
      sortOrder: "",
      isActive: true,
      link: "",
      updatedBy: "",
    });

    // Page State
    const isPageModalOpen = ref(false);
    const isEditPage = ref(false);
    const newPage = ref({
      pageName: '',
      pageTitle: '',
      pageDescription: '',
      isActive: true
    });

    // Page Content State
    const isPageContentModalOpen = ref(false);
    const isEditPageContent = ref(false);
    const newPageContent = ref({
      pageContentId: "",
      item: "",
      itemDescription: "",
      isActive: true,
      link: "",
      contentBlock: "",
      updatedBy: "",
    });

    // Subpage State
    const isSubpageModalOpen = ref(false);
    const isEditSubpage = ref(false);
    const newSubpage = ref({
      subPageId: "",
      subPageName: "",
      subPageTitle: "",
      subPageDescription: "",
      isActive: true,
      updatedBy: "",
    });

    // Subpage Content State
    const isSubpageContentModalOpen = ref(false);
    const isEditSubpageContent = ref(false);
    const newSubpageContent = ref({
      subPageContentId: "",
      item: "",
      itemDescription: "",
      isActive: true,
      link: "",
      contentBlock: "",
      updatedBy: "",
    });

    // confirmation dialog
    const isConfirmDialogOpen = ref(false);
    const confirmDialog = ref({
      title: "",
      message: "",
      onConfirm: null, // Function to execute on confirm
    });

    const openConfirmDialog = (title, message, onConfirm) => {
      confirmDialog.value = { title, message, onConfirm };
      isConfirmDialogOpen.value = true;
    };

    const closeConfirmDialog = () => {
      isConfirmDialogOpen.value = false;
    };

    const confirmAction = () => {
      if (confirmDialog.value.onConfirm) {
        confirmDialog.value.onConfirm(); // Execute the confirmed action
      }
      isConfirmDialogOpen.value = false; // Close the dialog
    };

    // Table headers
    const menuHeaders = [
      { title: "Menu Item", key: "menuItem" },
      { title: "Sort Order", key: "sortOrder" },
      { title: "isActive", key: "isActive" },
      { title: "Link", key: "link" },
      { title: "Updated By", key: "updatedBy" },
      { title: "Update Date", key: "updateDate" },
      { title: "Actions", key: "actions", align: "end" }
    ];

    const submenuHeaders = [
      { title: "Submenu Item", key: "subMenuItem" },
      { title: "Description", key: "subMenuDescription", class: "wrap-text-column" },
      { title: "isActive", key: "isActive" },
      { title: "Link", key: "link" },
      { title: "Sort Order", key: "sortOrder" },
      { title: "Updated By", key: "updatedBy" },
      { title: "Actions", key: "actions", align: "end" },
    ];

    const pageHeaders = [
      { title: "Page Name", key: "pageName" },
      { title: "Title", key: "pageTitle" },
      { title: "Description", key: "pageDescription", class: "wrap-text-column" },
      { title: "Content Block", key: "contentBlock" },
      { title: "isActive", key: "isActive" },
      { title: "Actions", key: "actions", align: "end" },
    ];

    const pageContentHeaders = [
      { title: "Item", key: "item" },
      { title: "Description", key: "itemDescription", class: "wrap-text-column" },
      { title: "Link", key: "link" },
      { title: "Content Block", key: "contentBlock" },
      { title: "isActive", key: "isActive" },
      { title: "Updated By", key: "updatedBy" },
      { title: "Update Date", key: "updateDate" },
      { title: "Actions", key: "actions", align: "end" },
    ];

    const subPageHeaders = [
      { title: "Subpage Name", key: "subPageName" },
      { title: "Title", key: "subPageTitle" },
      { title: "Description", key: "subPageDescription", class: "wrap-text-column" },
      { title: "isActive", key: "isActive" },
      { title: "Actions", key: "actions", align: "end" },
    ];

    const subPageContentHeaders = [
      { title: "Item", key: "item" },
      { title: "Description", key: "itemDescription", class: "wrap-text-column" },
      { title: "Link", key: "link" },
      { title: "Content Block", key: "contentBlock" },
      { title: "isActive", key: "isActive" },
      { title: "Actions", key: "actions", align: "end" },
    ];



    // ADD, EDIT, DELETE METHODS

    // General methods 
    const handleCancel = (level) => {
      if (level === 'menu') {
        isMenuModalOpen.value = false;
      } else if (level === 'submenu') {
        isSubmenuModalOpen.value = false;
      } else if (level === 'page') {
        isPageModalOpen.value = false;
      } else if (level === 'pageContent') {
        isPageContentModalOpen.value = false;
      } else if (level === 'subpage') {
        isSubpageModalOpen.value = false;
      } else if (level === 'subpageContent') {
        isSubpageContentModalOpen.value = false;
      } else {
        console.log('Unknown level, no action taken.');
      }
    };
    const getNextSortOrder = () => {
      if (!existingSortOrders.value || existingSortOrders.value.length === 0) {
        return 1; // Default to 1 if there are no sort orders
      }
      const maxSortOrder = Math.max(...existingSortOrders.value);
      return maxSortOrder + 1; // Increment the highest sort order
    };
    // MENU FUNCTION
    const addMenu = () => {
      // Reset form data
      newMenu.value = {
        menuId: "",
        menuItem: "",
        sortOrder: "",
        isActive: true,
        link: "",
        updatedBy: "",
      };
      isEditMenu.value = false;        // Add mode
      isMenuModalOpen.value = true;    // Open modal
    };

    const editMenu = (menu) => {
      if (!menu) return;
      // Copy the selected menu into newMenu for editing
      newMenu.value = { ...menu };
      isEditMenu.value = true;        // Edit mode
      isMenuModalOpen.value = true;   // Open modal
    };
    // const menuForm = ref(null);
    const validateMenuForm = () => {
      const errors = [];
      // Validate required fields
      const menuItemError = rules.required(newMenu.value.menuItem);
      console.log("menu item", menuItemError)
      if (menuItemError !== true) {
        errors.push(menuItemError); // Push error message if validation fails

      }

      const sortOrderError = rules.numeric(newMenu.value.sortOrder);
      if (sortOrderError !== true) {
        errors.push(sortOrderError);
      }

      if (errors.length > 0) {
        return false;
      }

      return true; // Validation passed
    };

    const saveMenu = async () => {
      if (!validateMenuForm()) {
        console.log("Validation failed. Form contains errors.");
        return; // Stop execution if validation fails
      }
      if (!newMenu.value.sortOrder) {
        newMenu.value.sortOrder = getNextSortOrder(); // Assign default sortOrder
      }
      if (isEditMenu.value) {
        // ---------- EDIT PAYLOAD ----------
        const editPayload = {
          menuId: newMenu.value.menuId,                  // Required: existing menu ID
          menuItem: newMenu.value.menuItem,              // Required: menu name
          sortOrder: parseInt(newMenu.value.sortOrder) || null, // Convert sortOrder to number
          isActive: newMenu.value.isActive ?? true,      // Default to true if undefined
          link: newMenu.value.link || null,              // Default to null if empty
          updatedBy: newMenu.value.updatedBy || "Anonymous" // If empty, use Anonymous
        };

        // 1) Show confirmation dialog
        openConfirmDialog(
          "Edit Menu",
          `Are you sure you want to save changes to "${newMenu.value.menuItem}"?`,
          async () => {
            try {
              // 2) On "Confirm," run the PUT request
              await axiosInstance.put(`/api/menu/${editPayload.menuId}`, editPayload);

              // Update the menu in the local state array reactively
              const index = menus.value.findIndex((m) => m.menuId === editPayload.menuId);
              if (index !== -1) {
                // Using splice ensures reactivity
                menus.value.splice(index, 1, { ...editPayload });
              }

              // Also update the currently selected menu reference if needed
              if (currentMenu.value && currentMenu.value.menuId === editPayload.menuId) {
                currentMenu.value = { ...editPayload };
              }

              isMenuModalOpen.value = false; // Close modal
            } catch (error) {
              console.error("Error editing menu:", error.response?.data || error.message);
              alert("Failed to edit menu. Please check the details.");
            }
          }
        );
      } else {
        // ---------- ADD PAYLOAD ----------
        const addPayload = {
          menuId: "", // or omit if backend auto-generates IDs
          menuItem: newMenu.value.menuItem,
          sortOrder: parseInt(newMenu.value.sortOrder) || null,
          isActive: newMenu.value.isActive ?? true,
          link: newMenu.value.link || null,
          updatedBy: newMenu.value.updatedBy || null,
        };

        try {
          const response = await axiosInstance.post("/api/menu", addPayload);
          menus.value.push(response.data); // Update local data
          isMenuModalOpen.value = false;    // Close modal
        } catch (error) {
          console.error("Error adding menu:", error.response?.data || error.message);
          alert("Failed to add menu. Please check the details.");
        }
      }
    };

    const toggleMenuActive = (menu) => {
      // We'll flip the existing isActive value:
      const newActiveState = !menu.isActive;

      // Optionally confirm with the user:
      openConfirmDialog(
        newActiveState ? "Enable Menu" : "Disable Menu",
        `Are you sure you want to set "${menu.menuItem}" to ${newActiveState ? "Active" : "Inactive"
        }?`,
        async () => {
          try {
            // We'll call the new endpoint or existing update endpoint:
            const payload = {
              isActive: newActiveState,
              updatedBy: "UpdatedByAdmin", // Or capture the current user
            };

            // If using a dedicated isActive endpoint:
            await axiosInstance.put(`/api/menu/${menu.menuId}/isActive`, payload);

            // OR if reusing the update endpoint:
            // await axiosInstance.put(`/api/menu/${menu.menuId}`, { ...menu, isActive: newActiveState });

            // Update local array to reflect the new state
            menu.isActive = newActiveState;
          } catch (error) {
            console.error("Failed to toggle active status:", error);
            alert("Could not change the active state. Please check logs.");
          }
        }
      );
    };


    // SUBMENU FUNCTION
    const addSubmenu = () => {
      if (!currentMenu.value) {
        console.error("No menu selected. Cannot add submenu.");
        return;
      }
      newSubmenu.value = { menuId: currentMenu.value.menuId, subMenuItem: "", sortOrder: "", isActive: true, link: "", updatedBy: "" }; // Reset the form
      isEditSubmenu.value = false; // Set to add mode
      isSubmenuModalOpen.value = true; // Open the modal
    };

    const editSubmenu = (submenu) => {
      newSubmenu.value = { ...submenu }; // Populate the form with submenu data
      isEditSubmenu.value = true; // Set to edit mode
      isSubmenuModalOpen.value = true; // Open the modal
    };
    // const menuForm = ref(null);
    const validateSubmenuForm = () => {
      const errors = [];
      // Validate required fields
      const menuItemError = rules.required(newSubmenu.value.subMenuItem);
      console.log("menu item", menuItemError)
      if (menuItemError !== true) {
        errors.push(menuItemError); // Push error message if validation fails

      }

      const linkError = rules.required(newSubmenu.value.link);
      if (linkError !== true) {
        errors.push(linkError);
      }

      if (errors.length > 0) {
        return false;
      }

      return true; // Validation passed
    };

    const saveSubmenu = async () => {

      if (!validateSubmenuForm()) {
        console.log("Validation failed. Form contains errors.");
        return; // Stop execution if validation fails
      }
      if (isEditSubmenu.value) {
        // ---------- EDIT PAYLOAD ----------
        const editPayload = {
          subMenuId: newSubmenu.value.subMenuId, // Required
          menuId: newSubmenu.value.menuId, // Required
          subMenuItem: newSubmenu.value.subMenuItem, // Required
          subMenuDescription: newSubmenu.value.subMenuDescription || "", // Optional
          sortOrder: Number(newSubmenu.value.sortOrder) || null, // Optional
          link: newSubmenu.value.link || null, // Optional
          isActive: newSubmenu.value.isActive ?? true, // Optional
          updatedBy: newSubmenu.value.updatedBy || "Anonymous", // Optional
        };

        // 1) Show confirmation dialog
        openConfirmDialog(
          "Edit Submenu",
          `Are you sure you want to save changes to "${newSubmenu.value.subMenuItem}"?`,
          async () => {
            try {
              // 2) PUT request after user confirms
              await axiosInstance.put(`/api/submenu/${newSubmenu.value.subMenuId}`, editPayload);

              // Update the submenu in the local list
              const index = currentMenu.value.submenus.findIndex(
                (s) => s.subMenuId === newSubmenu.value.subMenuId
              );
              if (index !== -1) {
                // Optionally replace with the entire payload, or just the fields
                currentMenu.value.submenus[index] = { ...newSubmenu.value };
              }

              // Close the modal
              isSubmenuModalOpen.value = false;
            } catch (error) {
              console.error("Error editing submenu:", error.response?.data || error.message);
              alert("Failed to edit submenu. Please check the details.");
            }
          }
        );
      } else {
        // ---------- ADD PAYLOAD ----------
        const addPayload = {
          submenuId: "", // or omit if backend auto-generates
          menuId: currentMenu.value.menuId,
          subMenuItem: newSubmenu.value.subMenuItem,
          subMenuDescription: newSubmenu.value.subMenuDescription || "",
          sortOrder: Number(newSubmenu.value.sortOrder) || null,
          link: newSubmenu.value.link || null,
          isActive: newSubmenu.value.isActive ?? true,
          updatedBy: newSubmenu.value.updatedBy || null,
        };

        // No confirmation for adding
        try {
          const response = await axiosInstance.post("/api/submenu", addPayload);
          currentMenu.value.submenus.push(response.data); // Update local data
          isSubmenuModalOpen.value = false;               // Close modal
        } catch (error) {
          console.error("Error saving submenu:", error.response?.data || error.message);
          alert("Failed to save submenu. Please check the details.");
        }

      }
    };

    const toggleSubmenuActive = (submenu) => {
      const newActiveState = !submenu.isActive;

      openConfirmDialog(
        newActiveState ? "Enable Submenu" : "Disable Submenu",
        `Are you sure you want to set "${submenu.subMenuItem}" to ${newActiveState ? "Active" : "Inactive"
        }?`,
        async () => {
          try {
            // Minimal payload for the toggle
            const payload = {
              isActive: newActiveState,
              updatedBy: "UpdatedByAdmin" // or track the actual user
            };

            // If using a dedicated isActive endpoint (like /api/submenu/{id}/isActive):
            await axiosInstance.put(`/api/submenu/${submenu.subMenuId}/isActive`, payload);

            // OR if reusing the main update endpoint:
            // await axiosInstance.put(`/api/submenu/${submenu.subMenuId}`, { ...submenu, isActive: newActiveState });

            // Reflect the change immediately in local data
            submenu.isActive = newActiveState;
          } catch (error) {
            console.error("Failed to toggle submenu active status:", error);
            alert("Could not change the submenu active state. Please check logs.");
          }
        }
      );
    };

    // PAGE FUNCTION
    const addPage = () => {
      if (!currentSubmenu.value) {
        console.error("No submenu selected. Cannot add page.");
        return;
      }
      newPage.value = { pageId: "", pageName: "", pageTitle: "", pageDescription: "", isActive: true };
      isEditPage.value = false; // Add mode
      isPageModalOpen.value = true; // Open modal
    };

    const editPage = (page) => {
      if (!page) return;
      newPage.value = { ...page };
      isEditPage.value = true; // Edit mode
      isPageModalOpen.value = true; // Open modal
    };

    const savePage = async () => {
      if (!newPage.value.pageName) {
        alert("Page Name is required.");
        return;
      }

      if (isEditPage.value) {
        const editPayload = { ...newPage.value };
        openConfirmDialog(
          "Edit Page",
          `Are you sure you want to save changes to "${newPage.value.pageName}"?`,
          async () => {
            try {
              await axiosInstance.put(`/api/page/${editPayload.pageId}`, editPayload);
              const index = currentSubmenu.value.pages.findIndex((p) => p.pageId === editPayload.pageId);
              if (index !== -1) {
                currentSubmenu.value.pages[index] = { ...editPayload };
              }
              isPageModalOpen.value = false;
            } catch (error) {
              console.error("Error editing page:", error.response?.data || error.message);
              alert("Failed to edit page. Please check the details.");
            }
          }
        );
      } else {
        const addPayload = { ...newPage.value, submenuId: currentSubmenu.value.subMenuId };
        try {
          const response = await axiosInstance.post("/api/page", addPayload);
          currentSubmenu.value.pages.push(response.data);
          isPageModalOpen.value = false;
        } catch (error) {
          console.error("Error adding page:", error.response?.data || error.message);
          alert("Failed to add page. Please check the details.");
        }
      }
    };

    const togglePageActive = (page) => {
      const newActiveState = !page.isActive;
      openConfirmDialog(
        newActiveState ? "Enable Page" : "Disable Page",
        `Are you sure you want to set "${page.pageName}" to ${newActiveState ? "Active" : "Inactive"}?`,
        async () => {
          try {
            const payload = { isActive: newActiveState, updatedBy: "Admin" };
            await axiosInstance.put(`/api/page/${page.pageId}/isActive`, payload);
            page.isActive = newActiveState;
          } catch (error) {
            console.error("Failed to toggle active status:", error);
            alert("Could not change the active state. Please check logs.");
          }
        }
      );
    };

    // PAGE CONTENT FUNCTION
    // Add Page Content
    const addPageContent = () => {
      if (!currentPage.value) {
        console.error("No page selected. Cannot add page content.");
        return;
      }
      newPageContent.value = {
        pageContentId: "",
        item: "",
        itemDescription: "",
        isActive: true,
        link: "",
        contentBlock: "",
        updatedBy: "",
      };
      isEditPageContent.value = false; // Add mode
      isPageContentModalOpen.value = true; // Open modal
    };

    // Edit Page Content
    const editPageContent = (pageContent) => {
      if (!pageContent) return;
      newPageContent.value = { ...pageContent }; // Populate form with existing data
      isEditPageContent.value = true; // Edit mode
      isPageContentModalOpen.value = true; // Open modal
    };

    // Save Page Content
    const savePageContent = async () => {
      if (!newPageContent.value.item) {
        alert("Item is required.");
        return;
      }

      if (isEditPageContent.value) {
        // Edit mode
        const editPayload = { ...newPageContent.value, pageId: currentPage.value.pageId };
        openConfirmDialog(
          "Edit Page Content",
          `Are you sure you want to save changes to "${newPageContent.value.item}"?`,
          async () => {
            try {
              await axiosInstance.put(`/api/pagecontent/${editPayload.pageContentId}`, editPayload);
              const index = currentPage.value.pagecontents.findIndex((c) => c.pageContentId === editPayload.pageContentId);
              if (index !== -1) {
                // Using splice ensures the update is reactive.
                currentPage.value.pagecontents.splice(index, 1, { ...editPayload });
              }
              isPageContentModalOpen.value = false;
            } catch (error) {
              console.error("Error editing page content:", error.response?.data || error.message);
              alert("Failed to edit page content. Please check the details.");
            }
          }
        );
      } else {
        // Add mode
        const addPayload = { ...newPageContent.value, pageId: currentPage.value.pageId };
        try {
          const response = await axiosInstance.post("/api/pagecontent", addPayload);
          currentPage.value.pagecontents.push(response.data);
          isPageContentModalOpen.value = false;
        } catch (error) {
          console.error("Error adding page content:", error.response?.data || error.message);
          alert("Failed to add page content. Please check the details.");
        }
      }
    };

    // Toggle Active Status
    const togglePageContentActive = (pageContent) => {
      const newActiveState = !pageContent.isActive;

      openConfirmDialog(
        newActiveState ? "Enable Page Content" : "Disable Page Content",
        `Are you sure you want to set "${pageContent.item}" to ${newActiveState ? "Active" : "Inactive"}?`,
        async () => {
          try {
            const payload = { isActive: newActiveState, updatedBy: "Admin" };
            await axiosInstance.put(`/api/pagecontent/${pageContent.pageContentId}/isActive`, payload);
            pageContent.isActive = newActiveState; // Update local state
          } catch (error) {
            console.error("Failed to toggle active status:", error);
            alert("Could not change the active state. Please check logs.");
          }
        }
      );
    };

    // SUBPAGE FUNCTION
    // Add Subpage
    const addSubpage = () => {
      if (!currentPageContent.value) {
        console.error("No page content selected. Cannot add subpage.");
        return;
      }
      newSubpage.value = {
        subPageId: "",
        subPageName: "",
        subPageTitle: "",
        subPageDescription: "",
        isActive: true,
        updatedBy: "",
      };
      isEditSubpage.value = false; // Add mode
      isSubpageModalOpen.value = true; // Open modal
    };

    // Edit Subpage
    const editSubpage = (subpage) => {
      if (!subpage) {
        return;
      }
      newSubpage.value = { ...subpage };
      isEditSubpage.value = true;
      isSubpageModalOpen.value = true;
    };

    // Save Subpage
    const saveSubpage = async () => {
      console.log(newSubpage.value);

      if (!newSubpage.value.subPageName) {
        alert("Subpage Name is required.");
        return;
      }

      if (isEditSubpage.value) {
        console.log(newSubpage.value);
        // Edit mode
        const editPayload = { ...newSubpage.value, pageContentId: currentPageContent.value.pageContentId };

        openConfirmDialog(
          "Edit Subpage",
          `Are you sure you want to save changes to "${newSubpage.value.subPageName}"?`,
          async () => {
            try {
              await axiosInstance.put(`/api/subpage/${editPayload.subPageId}`, editPayload);
              const index = currentPageContent.value.subpages.findIndex((s) => s.subPageId === editPayload.subPageId);
              if (index !== -1) {
                currentPageContent.value.subpages[index] = { ...editPayload };
              }
              isSubpageModalOpen.value = false;
            } catch (error) {
              console.error("Error editing subpage:", error.response?.data || error.message);
              alert("Failed to edit subpage. Please check the details.");
            }
          }
        );
      } else {
        // Add mode
        const addPayload = { ...newSubpage.value, pageContentId: currentPageContent.value.pageContentId };
        try {
          const response = await axiosInstance.post("/api/subpage", addPayload);
          currentPageContent.value.subpages.push(response.data);
          isSubpageModalOpen.value = false;
        } catch (error) {
          console.error("Error adding subpage:", error.response?.data || error.message);
          alert("Failed to add subpage. Please check the details.");
        }
      }
    };

    // Toggle Active Status
    const toggleSubpageActive = (subpage) => {
      if (!subpage || !subpage.subPageId) {
        console.error("Invalid subpage object:", subpage);
        return;
      }
      console.log("Toggling subpage active state:", subpage); // Debug
      const newActiveState = !subpage.isActive;

      openConfirmDialog(
        newActiveState ? "Enable Subpage" : "Disable Subpage",
        `Are you sure you want to set "${subpage.subPageName}" to ${newActiveState ? "Active" : "Inactive"}?`,
        async () => {
          try {
            const payload = { isActive: newActiveState, updatedBy: "Admin" };
            await axiosInstance.put(`/api/subpage/${subpage.subPageId}/isActive`, payload);
            subpage.isActive = newActiveState; // Update local state
          } catch (error) {
            console.error("Failed to toggle active status:", error);
            alert("Could not change the active state. Please check logs.");
          }
        }
      );
    };

    // SUBPAGE CONTENT FUNCTION
    // Add Subpage Content
    const addSubpageContent = () => {
      if (!currentSubpage.value) {
        console.error("No subpage selected. Cannot add subpage content.");
        return;
      }
      newSubpageContent.value = {
        subpageContentId: "",
        item: "",
        itemDescription: "",
        isActive: true,
        link: "",
        contentBlock: "",
        updatedBy: "",
      };
      isEditSubpageContent.value = false; // Add mode
      isSubpageContentModalOpen.value = true; // Open modal
    };

    // Edit Subpage Content
    const editSubpageContent = (subpageContent) => {
      if (!subpageContent) return;
      newSubpageContent.value = { ...subpageContent }; // Populate form with existing data
      isEditSubpageContent.value = true; // Edit mode
      isSubpageContentModalOpen.value = true; // Open modal
    };

    // Save Subpage Content
    const saveSubpageContent = async () => {
      if (!newSubpageContent.value.item) {
        alert("Content Name is required.");
        return;
      }

      if (isEditSubpageContent.value) {
        // Edit mode
        const editPayload = { ...newSubpageContent.value, subPageId: currentSubpage.value.subPageId };

        openConfirmDialog(
          "Edit Subpage Content",
          `Are you sure you want to save changes to "${newSubpageContent.value.item}"?`,
          async () => {
            try {
              await axiosInstance.put(
                `/api/subpagecontent/${editPayload.subPageContentId}`,
                editPayload
              );

              const index = currentSubpage.value.subpagecontents.findIndex(
                (sc) => sc.subPageContentId === editPayload.subPageContentId
              );
              if (index !== -1) {
                // Splice for reactivity
                currentSubpage.value.subpagecontents.splice(index, 1, { ...editPayload });
              }
              isSubpageContentModalOpen.value = false;
            } catch (error) {
              console.error("Error editing subpage content:", error.response?.data || error.message);
              alert("Failed to edit subpage content. Please check the details.");
            }
          }
        );
      } else {
        // Add mode
        const addPayload = { ...newSubpageContent.value, subPageId: currentSubpage.value.subPageId };
        try {
          const response = await axiosInstance.post("/api/subpagecontent", addPayload);
          // Ensure you push the new item into the reactive array
          currentSubpage.value.subpagecontents.push(response.data);
          isSubpageContentModalOpen.value = false;
        } catch (error) {
          console.error("Error adding subpage content:", error.response?.data || error.message);
          alert("Failed to add subpage content. Please check the details.");
        }
      }
    };

    // Toggle Active Status
    const toggleSubpageContentActive = (subpageContent) => {
      const newActiveState = !subpageContent.isActive;

      openConfirmDialog(
        newActiveState ? "Enable Subpage Content" : "Disable Subpage Content",
        `Are you sure you want to set "${subpageContent.item}" to ${newActiveState ? "Active" : "Inactive"
        }?`,
        async () => {
          try {
            const payload = { isActive: newActiveState, updatedBy: "Admin" };
            await axiosInstance.put(`/api/subpagecontent/${subpageContent.subPageContentId}/isActive`, payload);
            subpageContent.isActive = newActiveState; // Update local state
          } catch (error) {
            console.error("Failed to toggle active status:", error);
            alert("Could not change the active state. Please check logs.");
          }
        }
      );
    };

    // RULES
    const rules = {
      required: (value) => !!value || "This field is required.",
      numeric: (value) => !isNaN(Number(value)) || "Must be a number.",
      optional: () => true, // Always pass for optional fields
      uniqueSortOrder: (value) => {
        if (!existingSortOrders.value) return true; // Skip validation if list is not ready
        return !existingSortOrders.value.includes(Number(value)) || "Sort Order must be unique.";
      }
    };

    // CALL APIS METHOD
    const fetchMenus = async () => {
      try {
        const response = await axiosInstance.get("/api/menu/all");
        menus.value = response.data;
        existingSortOrders.value = response.data
          .map((menu) => menu.sortOrder)
          .filter((order) => order !== null && order !== undefined);
      } catch (error) {
        console.error("Error fetching menus:", error.response || error);
      }
    };

    const fetchSubmenus = async (menuId) => {
      try {
        const response = await axiosInstance.get(`/api/submenu/all?menuId=${menuId}`);
        currentMenu.value.submenus = response.data;
      } catch (error) {
        console.error("Error fetching submenus:", error.response || error);
      }
    };

    const fetchPages = async (submenuId) => {
      try {
        const response = await axiosInstance.get(`/api/page/all?submenuId=${submenuId}`);
        currentSubmenu.value.pages = response.data;
      } catch (error) {
        console.error("Error fetching pages:", error);
      }
    };

    const fetchPageContents = async (pageId) => {
      try {
        const response = await axiosInstance.get(`/api/pagecontent/all?pageId=${pageId}`);
        console.log("Fetched Page Contents for Page ID:", pageId, response.data);

        currentPage.value.pagecontents = response.data.map((item) => ({
          ...item,
          showFullLink: false,
        }));
      } catch (error) {
        console.error("Error fetching page contents:", error);
      }
    };

    const fetchSubpages = async (pageContentId) => {
      try {
        const response = await axiosInstance.get(`/api/subpage/all?pageContentId=${pageContentId}`);
        currentPageContent.value.subpages = response.data;
      } catch (error) {
        console.error("Error fetching subpages:", error);
      }
    };

    const fetchSubpageContents = async (subPageId) => {
      try {
        const response = await axiosInstance.get(`/api/subpagecontent/all?subPageId=${subPageId}`);
        currentSubpage.value.subpagecontents = response.data.map((item) => ({
          ...item,
          showFullLink: false,
        }));
      } catch (error) {
        console.error("Error fetching subpage contents:", error);
      }
    };


    // Navigation handlers
    const selectMenu = async (clickEvent, menu) => {
      if (!menu) return;

      currentMenu.value = menu.item;
      currentLevel.value = "submenu";
      updateBreadcrumbs(1, menu.item.menuItem);

      if (!currentMenu.value.submenus || currentMenu.value.submenus.length === 0) {
        try {
          await fetchSubmenus(menu.item.menuId);
        } catch (error) {
          console.error("Error fetching submenus:", error);
        }
      }
    };

    const selectSubmenu = async (clickEvent, submenu) => {
      if (!submenu) return;

      currentSubmenu.value = submenu.item;
      currentLevel.value = "page";
      updateBreadcrumbs(2, submenu.item.subMenuItem);

      // Ensure pages are fetched only if not already loaded
      if (!currentSubmenu.value.pages || currentSubmenu.value.pages.length === 0) {
        try {
          await fetchPages(submenu.item.subMenuId); // Fetch pages
        } catch (error) {
          console.error("Error fetching pages:", error);
        }
      }
    };

    const selectPage = async (clickEvent, page) => {

      if (!page) return;


      currentPage.value = page.item;
      currentLevel.value = "pagecontent";
      updateBreadcrumbs(3, page.item.pageName);

      // Ensure page contents are fetched only if not already loaded
      if (!currentPage.value.pagecontents || currentPage.value.pagecontents.length === 0) {
        try {
          await fetchPageContents(page.item.pageId); // Fetch page contents
        } catch (error) {
          console.error("Error fetching page contents:", error);
        }
      }
    };

    const selectPageContent = async (clickEvent, content) => {
      if (!content) return;

      currentPageContent.value = content.item;
      currentLevel.value = "subpage";
      updateBreadcrumbs(4, content.item.item);

      // Ensure subpages are fetched only if not already loaded
      if (!currentPageContent.value.subpages || currentPageContent.value.subpages.length === 0) {
        try {
          await fetchSubpages(content.item.pageContentId); // Fetch subpages
        } catch (error) {
          console.error("Error fetching subpages:", error);
        }
      }
    };

    const selectSubpage = async (clickEvent, subpage) => {
      if (!subpage) return;

      currentSubpage.value = subpage.item;
      currentLevel.value = "subpagecontent";
      updateBreadcrumbs(5, subpage.item.subPageName);

      // Ensure subpage contents are fetched only if not already loaded
      if (!currentSubpage.value.subpagecontents || currentSubpage.value.subpagecontents.length === 0) {
        try {
          await fetchSubpageContents(subpage.item.subPageId); // Fetch subpage contents
        } catch (error) {
          console.error("Error fetching subpage contents:", error);
        }
      }
    };

    // truncate and toggle long links functions
    const truncate = (text, length = 20) => {
      return text.length > length ? text.slice(0, length) + "..." : text;
    };

    const toggleShowFullLink = (item) => {
      item.showFullLink = !item.showFullLink;
    };

    const copyToClipboard = (link) => {
      navigator.clipboard
        .writeText(link)
        .then(() => {
          // Optionally, show a notification
          this.$emit("show-snackbar", "Link copied to clipboard!");
        })
        .catch((err) => {
          console.error("Failed to copy link: ", err);
        });
    }

    // GO BACK
    const goBack = () => {
      breadcrumbs.value.pop();
      const lastBreadcrumb = breadcrumbs.value[breadcrumbs.value.length - 1] || { level: 0 };
      navigateTo(lastBreadcrumb.level);
    };
    // const goBack = () => {
    //   if (currentLevel.value === "submenu") {
    //     currentMenu.value = null;
    //     currentLevel.value = "menu";
    //   } else if (currentLevel.value === "page") {
    //     currentSubmenu.value = null;
    //     currentLevel.value = "submenu";
    //   } else if (currentLevel.value === "pagecontent") {
    //     currentPage.value = null;
    //     currentLevel.value = "page";
    //   } else if (currentLevel.value === "subpage") {
    //     currentPageContent.value = null;
    //     currentLevel.value = "pagecontent";
    //   } else if (currentLevel.value === "subpagecontent") {
    //     currentSubpage.value = null;
    //     currentLevel.value = "subpage";
    //   }
    // };

    // Use the onMounted lifecycle hook to fetch initial data
    onMounted(async () => {
      document.title = "MendMate-MenuCRUD";
      breadcrumbs.value = [{ level: 0, label: "Home" }];
      await fetchMenus();
    });

    const blockContentTypes = ["Items", "Reading", "Video", "Worksheet"];
    
    // Direct logout handler in case the NavBar isn't visible
    const handleDirectLogout = async () => {
      try {
        const response = await axiosInstance.post("/api/auth/logout", null, {
          withCredentials: true,
        });
        if (response.status === 200) {
          localStorage.clear();  // Clear all local storage in the domain
          window.location.href = "/login";  // Redirect to login page
        } else {
          console.error(`Logout failed: ${response.status}`);
          alert("Logout failed. Please try again.");
        }
      } catch (error) {
        console.error("Logout error:", error);
        alert("Logout failed. Please try again.");
      }
    };

    return {
      menus,
      currentMenu,
      currentSubmenu,
      currentPage,
      currentPageContent,
      currentSubpage,
      currentLevel,
      search,
      // headers
      menuHeaders,
      submenuHeaders,
      pageHeaders,
      pageContentHeaders,
      subPageHeaders,
      subPageContentHeaders,
      // Handlers
      selectMenu,
      selectSubmenu,
      selectPage,
      selectPageContent,
      selectSubpage,
      goBack,

      // others
      truncate,
      toggleShowFullLink,
      copyToClipboard,


      // edit/delete/new
      // menu
      editMenu,
      saveMenu,
      rules,
      isMenuModalOpen,
      isEditMenu,
      newMenu,
      addMenu,
      toggleMenuActive,

      //submenu
      isSubmenuModalOpen,
      isEditSubmenu,
      newSubmenu,
      addSubmenu,
      editSubmenu,
      saveSubmenu,
      toggleSubmenuActive,

      // page
      isPageModalOpen,
      isEditPage,
      newPage,
      addPage,
      editPage,
      savePage,
      togglePageActive,

      // pageContent
      isPageContentModalOpen,
      isEditPageContent,
      newPageContent,
      addPageContent,
      editPageContent,
      savePageContent,
      togglePageContentActive,

      // subpage
      isSubpageModalOpen,
      isEditSubpage,
      newSubpage,
      addSubpage,
      editSubpage,
      saveSubpage,
      toggleSubpageActive,

      // subpage content
      isSubpageContentModalOpen,
      isEditSubpageContent,
      newSubpageContent,
      addSubpageContent,
      editSubpageContent,
      saveSubpageContent,
      toggleSubpageContentActive,

      // breadcrumbs
      breadcrumbs,
      updateBreadcrumbs,
      navigateTo,

      // cancel
      handleCancel,

      // confirmation dialog
      isConfirmDialogOpen,
      confirmDialog,
      openConfirmDialog,
      closeConfirmDialog,
      confirmAction,

      blockContentTypes,
      
      // Add logout handler to the return object
      handleDirectLogout,
    };
  },
};
</script>

<style scoped>
* {
  font-family: 'Montserrat';
}

.container {
  overflow: hidden;
  height: 100vh;
}

/* Admin navbar styling */
.navbar {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  background-color: white !important;
}

.nav-title {
  color: #333;
  font-weight: 500;
  font-size: 1.2rem;
}

.profile-btn {
  transition: transform 0.2s ease;
}

.profile-btn:hover {
  transform: scale(1.1);
}

.logout-item {
  transition: background-color 0.2s ease;
}

.logout-item:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

/* Add space between navbar and content */
.content-container {
  padding-top: 24px;
}

/* Add color to table headers */
.v-data-table thead th {
  background-color: #4CAF50;
  /* Green background */
  color: white;
  /* White text */
  font-weight: bold;
  /* Bold font */
  text-align: center;
  /* Center text */
  padding: 12px;
  /* Padding for spacing */
  border-bottom: 2px solid #388E3C;
  /* Border for distinction */
}

/* Optional hover effect */
.v-data-table thead th:hover {
  background-color: #388E3C;
  /* Darker green on hover */
  cursor: pointer;
  /* Pointer cursor on hover */
}

.truncate-text {
  display: inline-block;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
}

.truncate-text:hover {
  background-color: yellow;
}

.expanded-text:hover {
  cursor: pointer;
  background-color: yellow;
}

/* copy icon */
.v-icon.subtle-icon {
  font-size: 18px;
  /* Smaller size */
  color: grey;
  /* Subtle color */
  cursor: pointer;
  /* Pointer cursor for clarity */
  padding: 0;
  /* Remove any padding */
}

.v-icon.subtle-icon:hover {
  color: black;
  /* Slightly darker on hover */
}

.v-toolbar {
  background-color: #f5f5f5;
  padding: 10px;
}

.v-card {
  padding: 5px;
}

/* wrap-text-description */
.wrap-text {
  max-width: 400px;
  /* Adjust the max width as needed */
  word-wrap: break-word;
  white-space: normal;
}

/* breadcrumbs */
.breadcrumbs-wrapper {
  padding: 8px 8px;
  background-color: #f9f9f9;
  border-bottom: 1px solid #ddd;
  display: flex;
  align-items: center;
  font-size: 20px;
}

.breadcrumbs {
  display: flex;
  align-items: center;
  color: #555;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #007bff;
}

.breadcrumb-item:hover .breadcrumb-link {
  text-decoration: underline;
  color: #0056b3;
}

.breadcrumb-link {
  color: #007bff;
  transition: color 0.2s ease;
}

.breadcrumb-separator {
  color: #888;
  margin: 0 4px;
  font-size: 18px;
}

.breadcrumb-active {
  color: #333;
  font-weight: bold;
  cursor: default;
}

.breadcrumb-active:hover {
  text-decoration: none;
}

/* menu-management */
.header-section {
  padding: 16px;
  background-color: white;
  border-bottom: 1px solid #ddd;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.buttons-search-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.button-wrapper {
  flex-grow: 1;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.search-wrapper {
  flex-shrink: 0;
}

.search-input {
  min-width: 600px;
  max-width: 100%;
}

.page-title {
  font-size: 25px;
  margin: 0;
  flex-grow: 1;

}

.table-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: auto;
}

/* Button Styles */
.v-btn {
  font-size: 16px;
  /* Larger font for better readability */
  padding: 10px 20px;
  /* Consistent padding for clickability */
  border-radius: 8px;
  /* Rounded corners for a softer look */
  transition: all 0.3s ease;
  /* Smooth transitions for hover effects */
}

.v-btn.primary {
  background-color: #007bff;
  /* Primary blue */
  color: white;
}

.v-btn.primary:hover {
  background-color: #0056b3;
  /* Darker blue on hover */
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.2);
  /* Subtle shadow */
}

.v-btn.primary:active {
  background-color: #004080;
  /* Even darker blue on active */
}

.v-btn.secondary {
  background-color: #6c757d;
  /* Secondary grey */
  color: white;
}

.v-btn.secondary:hover {
  background-color: #5a6268;
  /* Darker grey on hover */
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.2);
  /* Subtle shadow */
}

.v-btn.secondary:active {
  background-color: #343a40;
  /* Even darker grey on active */
}

/* Focus state for accessibility */
.v-btn:focus {
  outline: 3px solid #80bdff;
  /* Highlight outline for focused button */
  outline-offset: 2px;
  /* Offset outline for clarity */
}

/* Icons and labels */
.v-btn .v-icon {
  margin-right: 8px;
  /* Space between icon and text */
}

/* Disabled button style */
.v-btn:disabled {
  background-color: #e0e0e0;
  /* Light grey for disabled state */
  color: #bdbdbd;
  /* Text color for disabled state */
  cursor: not-allowed;
}

/* Actions Column Button Styles */
.v-btn.icon {
  transition: all 0.3s ease;
  /* Smooth transition */
}

/* Active Button (Green) */
.btn-active {
  background-color: #28a745;
  /* Green background for active */
  color: white;
  /* White icon color */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  /* Subtle shadow */
}

.btn-active:hover {
  background-color: #218838;
  /* Darker green on hover */
}

/* Inactive Button */
.btn-inactive {
  background-color: #e0e0e0;
  /* Light grey background for inactive */
  color: #6c757d;
  /* Grey icon color */
}

.btn-inactive:hover {
  background-color: #d6d6d6;
  /* Slightly darker grey on hover */
}

.action-buttons {
  display: flex;
  gap: 8px;
  /* Adds space between buttons */
  align-items: center;
  /* Aligns buttons vertically */
  margin-left: auto;
  /* Pushes the buttons to the right */
  justify-content: flex-end;
  /* Ensures buttons align at the right edge */

}

.user-actions {
  display: flex;
  align-items: center;
  margin-left: auto;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.page-title {
  font-size: 25px;
  margin: 0;
}
</style>